import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, ArrowRight, AlertTriangle } from "lucide-react";

// CTA banner mostrato quando l'utente ha raggiunto il ≥80% della quota
// (schede o immobili) e non è ancora Pro/Admin. Cliccabile → /pricing.
// Si nasconde da solo se l'utente è già Pro/Admin o se ha una quota illimitata.
const THRESHOLD = 0.8;

const UpgradeCTABanner = ({ className = "" }) => {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const d = window.localStorage.getItem("cta_upgrade_dismissed_at");
      if (d) {
        const age = Date.now() - Number(d);
        // Rispetta il dismiss per 24h
        if (age > 0 && age < 24 * 3600 * 1000) setDismissed(true);
      }
    } catch { /* ignore */ }
    (async () => {
      try {
        const r = await api.get("/users/me/quota");
        setData(r.data);
      } catch (err) {
        console.warn("[UpgradeCTABanner] fetch failed:", err);
      }
    })();
  }, []);

  if (!data || dismissed) return null;
  const tier = (data.tier || "demo").toLowerCase();
  if (tier === "pro") return null;

  const ratio = (used, limit) => (limit === -1 || !limit) ? 0 : used / limit;
  const rBoards = ratio(data.boards.used, data.boards.limit);
  const rProps  = ratio(data.properties.used, data.properties.limit);
  const worst   = Math.max(rBoards, rProps);
  if (worst < THRESHOLD) return null;

  const hitBoards = rBoards >= 1;
  const hitProps  = rProps  >= 1;
  const hardLimit = hitBoards || hitProps;

  const which = rBoards >= rProps
    ? { label: "schede", used: data.boards.used, limit: data.boards.limit, pct: Math.round(rBoards * 100) }
    : { label: "immobili", used: data.properties.used, limit: data.properties.limit, pct: Math.round(rProps * 100) };

  const dismiss = (e) => {
    e.preventDefault();
    try { window.localStorage.setItem("cta_upgrade_dismissed_at", String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center gap-4 shadow-lg
        ${hardLimit
          ? "bg-gradient-to-r from-rose-600 via-rose-500 to-orange-500 text-white"
          : "bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 text-white"}
        ${className}`}
      data-testid="upgrade-cta-banner"
    >
      {/* Decorative sparkle overlay */}
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 0, transparent 40%), radial-gradient(circle at 80% 70%, #fff 0, transparent 40%)" }} />
      <div className="relative shrink-0 w-11 h-11 rounded-2xl bg-white/25 backdrop-blur-sm flex items-center justify-center border border-white/30">
        {hardLimit ? <AlertTriangle className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
      </div>
      <div className="relative flex-1 min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] opacity-90">
          {hardLimit ? "Quota raggiunta" : `Sei al ${which.pct}% della tua quota`}
        </div>
        <div className="font-display font-bold text-base md:text-lg leading-tight mt-0.5">
          {hardLimit
            ? `Hai esaurito le ${which.label} del tuo piano ${tier.toUpperCase()} (${which.used}/${which.limit}).`
            : `Stai per esaurire le ${which.label} del tuo piano ${tier.toUpperCase()} (${which.used}/${which.limit}).`}
        </div>
        <div className="text-xs md:text-sm mt-1 opacity-95">
          Diventa <b>Pro</b> per sbloccare quote illimitate, il <b>Piano Economico</b>, l'export avanzato e l'AI Screenshot.
        </div>
      </div>
      <div className="relative flex items-center gap-2 shrink-0">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-slate-900 font-semibold text-sm hover:bg-slate-100 transition-colors shadow-sm"
          data-testid="upgrade-cta-btn"
        >
          Diventa Pro <ArrowRight className="w-4 h-4" />
        </Link>
        <button
          onClick={dismiss}
          className="text-white/80 hover:text-white text-xs underline underline-offset-2 px-1"
          data-testid="upgrade-cta-dismiss"
          title="Nascondi per 24h"
        >
          Più tardi
        </button>
      </div>
    </div>
  );
};

export default UpgradeCTABanner;
